import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import taskService from '../../services/taskService';
import userService from '../../services/userService';
import projectService from '../../services/projectService';
import { FaProjectDiagram, FaUserAlt, FaClock, FaExclamationCircle, FaTasks } from 'react-icons/fa';
import '../../styles/taskManagement.css';

const TaskForm = ({ task, onSubmitSuccess, initialProjectId = null, loading: parentLoading = false }) => {
  const { currentUser } = useAuth();
  const isEdit = !!task;
  
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    project_id: task?.project_id || initialProjectId || '',
    assigned_to: task?.assigned_to || '',
    expected_hours: task?.expected_hours || '',
    status: task?.status || 'pending'
  });
  
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(parentLoading);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch users and projects in parallel
        const [usersResponse, projectsResponse] = await Promise.all([
          userService.getAllUsers(),
          projectService.getAllProjects()
        ]);
        
        setUsers(usersResponse || []);
        setProjects(projectsResponse || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching form data:', err);
        setError('Failed to load form data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Update formData if initialProjectId changes (e.g. when project is loaded)
  useEffect(() => {
    if (initialProjectId && initialProjectId !== formData.project_id) {
      setFormData(prevData => ({
        ...prevData,
        project_id: initialProjectId
      }));
    }
  }, [initialProjectId, formData.project_id]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      if (isEdit) {
        // Update existing task
        await taskService.updateTask(task.id, formData);
      } else {
        // Create new task
        await taskService.createTask(formData);
      }
      
      setLoading(false);
      if (onSubmitSuccess) onSubmitSuccess();
    } catch (err) {
      console.error('Error submitting task:', err);
      setError(err.response?.data?.error || 'Failed to save task. Please try again.');
      setLoading(false);
    }
  };
  {console.log("currentUser",currentUser)}
  if (currentUser.role !== 'admin' && currentUser.role !== 'manager') {
    return (
      <div className="error-container">
        <div className="error-content">
          <FaExclamationCircle className="error-icon" />
          <p className="error-message">Only admins can create or edit tasks.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      {error && (
        <div className="form-error-message">
          <FaExclamationCircle className="error-icon" />
          <p>{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="task-form">
        <div className="form-group">
          <label className="form-label" htmlFor="title">
            <FaTasks className="form-label-icon" />
            <span data-required="*">Task Title</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="form-input"
            placeholder="Enter task title"
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label" htmlFor="description">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="form-textarea"
            placeholder="Enter detailed description of the task"
            rows="5"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label" htmlFor="project_id">
            <FaProjectDiagram className="form-label-icon" />
            <span data-required="*">Project</span>
          </label>
          <select
            id="project_id"
            name="project_id"
            value={formData.project_id}
            onChange={handleChange}
            className="form-select"
            required
            disabled={initialProjectId !== null}
          >
            <option value="">Select a project</option>
            {projects.map(project => {
              // Handle both id and _id formats
              const projectId = project.id || project._id;
              return (
                <option key={projectId} value={projectId}>
                  {project.name}
                </option>
              );
            })}
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label" htmlFor="assigned_to">
            <FaUserAlt className="form-label-icon" />
            <span data-required="*">Assign To</span>
          </label>
          <select
            id="assigned_to"
            name="assigned_to"
            value={formData.assigned_to}
            onChange={handleChange}
            className="form-select"
            required
          >
            <option value="">Select a user</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.role})
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label" htmlFor="expected_hours">
            <FaClock className="form-label-icon" />
            <span data-required="*">Expected Hours</span>
          </label>
          <input
            type="number"
            id="expected_hours"
            name="expected_hours"
            value={formData.expected_hours}
            onChange={handleChange}
            className="form-input"
            min="0.1"
            step="0.1"
            placeholder="Enter estimated hours"
            required
          />
          <p className="form-help-text">
            This estimate is visible only to admins
          </p>
        </div>
        
        {isEdit && (
          <div className="form-group">
            <label className="form-label" htmlFor="status">
              <FaExclamationCircle className="form-label-icon" />
              <span>Status</span>
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="form-select"
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        )}
        
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="btn-spinner"></div>
                <span>Saving...</span>
              </>
            ) : (
              <span>{isEdit ? 'Update Task' : 'Create Task'}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskForm;
